import './styles.scss';

import { AccidentHistoryRow, useAccidentHistory } from '@/features/AccidentHistory';
import { Datetime, generateSearchableString } from '@/utils';

import { AccidentHistoryFormDefinitions } from '../AccidentHistoryForm/types';
import AccidentHistoryTableColumns from '@/features/AccidentHistory/components/AccidentHistoryTable/AccidentHistoryTableColumns';
import AccidentHistoryTableRow from '@/features/AccidentHistory/components/AccidentHistoryTable/AccidentHistoryTableRow';
import SimpleTable from '@/components/SimpleTable';
import { SortDirectionEnum } from '@/models/gen/graphql';

const AccidentHistoryTable = (): JSX.Element => {
  const [{ accidents: sortedAccidents }] = useAccidentHistory(({ accidents, sortBy, sortDirection, search = '' }) => ({
    accidents: Array.from(accidents.keys())
      .filter((id: string) => {
        if ((search ?? '') === '') return true;
        const accident = accidents.get(id);
        if (!accident) return false;
        const searchable = generateSearchableString(accident, AccidentHistoryFormDefinitions.searchable);
        return searchable.includes(search?.toLowerCase?.());
      })
      .sort((a: string, b: string): number => {
        if (!sortDirection || !sortBy) return 0;
        const sortAccidents = compareAccidentsBy(sortBy);
        const result = sortAccidents(accidents.get(a), accidents.get(b));
        if (sortDirection === SortDirectionEnum.Desc) return -result;
        return result;
      }),
  }));

  return (
    <SimpleTable
      className="AccidentHistoryTable"
      rows={sortedAccidents}
      columns={AccidentHistoryTableColumns}
      row={AccidentHistoryTableRow}
    />
  );
};

const compareAccidentsBy =
  (sortBy: string) =>
  (accidentA: AccidentHistoryRow, accidentB: AccidentHistoryRow): number => {
    if (!accidentA || !accidentB) return 0;
    switch (sortBy) {
      case 'STATION_TEXT':
        return accidentA.stationText.localeCompare(accidentB.stationText);
      case 'ACCIDENT_DATETIME':
        return new Datetime(accidentA.accidentDatetime).diff(accidentB.accidentDatetime, 'millisecond');
      case 'CREATED_AT':
        return new Datetime(accidentA.createdAt).diff(accidentB.createdAt, 'millisecond');
      case 'TRIP_SERVICER_IATA_AIRLINE_CODE':
        return accidentA.tripServicerIataAirlineCode.localeCompare(accidentB.tripServicerIataAirlineCode);
      case 'TRIP_FLIGHT_NAME':
        let flightNameA = accidentA.tripLoopName || accidentA.tripFlightNumber.toString();
        let flightNameB = accidentB.tripLoopName || accidentB.tripFlightNumber.toString();
        return flightNameA.localeCompare(flightNameB);
      case 'TRIP_SCHEDULED':
        return new Datetime(accidentA.tripScheduled).diff(accidentB.tripScheduled, 'millisecond');
      case 'VEHICLE_TRACKING_ID':
        return accidentA.vehicleId.localeCompare(accidentB.vehicleId);
      case 'DRIVER_NAME':
        return accidentA.driverId.localeCompare(accidentB.driverId);
      case 'DRIVER_EMPLOYEE_ID':
        return accidentA.driverEmployeeId.localeCompare(accidentB.driverEmployeeId);
      case 'SAFETY_NAME':
        return accidentA.safetyId.localeCompare(accidentB.safetyId);
      case 'ANYONE_INJURED':
        return accidentA.anyoneInjured === accidentB.anyoneInjured ? 0 : accidentA.anyoneInjured ? -1 : 1;
      case 'WAS_CREW_INVOLVED':
        return accidentA.wasCrewInvolved === accidentB.wasCrewInvolved ? 0 : accidentA.wasCrewInvolved ? -1 : 1;
      default:
        return 0;
    }
  };

export default AccidentHistoryTable;
